import React, { useState, useEffect } from 'react';
import './App.css';
import DevWarningBanner from './components/DevWarningBanner';
import EntitySelector from './components/EntitySelector';
import PullTBPanel from './components/PullTBPanel';
import StatusPanel from './components/StatusPanel';
import FetchGLPanel from './components/FetchGLPanel';
import GLListPanel from './components/GLListPanelEnhanced';
import RPTV3GLNatureMappingPanel from './components/RPTV3GLNatureMappingPanel';
import GenerateRPTModal from './components/GenerateRPTModal';
import GenerateRPTV2Modal from './components/GenerateRPTV2Modal';
import GenerateRPTV3Modal from './components/GenerateRPTV3Modal';
import ViewGLModal from './components/ViewGLModal';
import QueryPanel from './components/QueryPanel';
import LeaseManagementPanel from './components/LeaseManagementPanel';
import FXRatesPanel from './components/FXRatesPanel';
import PPEManagementPanel from './components/PPEManagementPanel';
import RetainedEarningsPanel from './components/RetainedEarningsPanel';
import FDManagementPanel from './components/FDManagementPanel';
import BorrowingManagementPanel from './components/BorrowingManagementPanel';
import FCTRPanel from './components/FCTRPanel';
import SOCIEPanel from './components/SOCIEPanel';
import ESOPPanel from './components/ESOPPanel';
import TemplateValidationPanel from './components/TemplateValidationPanel';
import ARAgingPanel from './components/ARAgingPanel';
import APAgingPanel from './components/APAgingPanel';
import ARConsolidationPanel from './components/ARConsolidationPanel';
import APConsolidationPanel from './components/APConsolidationPanel';
import ELARConsolidationPanel from './components/ELARConsolidationPanel';
import ELAPConsolidationPanel from './components/ELAPConsolidationPanel';
import JournalAdjustmentsPanel from './components/JournalAdjustmentsPanel';
import ConsolAdjustmentsPanel from './components/ConsolAdjustmentsPanel';
import CashflowPanel from './components/CashflowPanel';
import IntercompanyPanel from './components/IntercompanyPanel';
import TLRPTModal from './components/TLRPTModal';
import { sapApi, healthCheck } from './services/api';

function App() {
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState({ healthy: false, environment: 'unknown' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedDataPoint, setSelectedDataPoint] = useState('tb'); // 'tb', 'rpt', 'forex', 'ppe', 'lease', 'rou_finalyzer', 'retained_earnings', 'cashflow', 'soce', 'esop', 'fctr', 'fd_working', 'borrowings', 'ar_aging', 'ap_aging', 'ar_aging_consol', 'ap_aging_consol', 'templates', or 'query'

  // RPT workflow state (kept for backward compatibility but not actively used in new UI)
  const [glInfo, setGlInfo] = useState(null);
  const [draftInfo, setDraftInfo] = useState(null);
  const [rptInfo, setRptInfo] = useState(null);

  // TL Group RPT modal
  const [showTLRPTModal, setShowTLRPTModal] = useState(false);

  // Modal state for new table-based UI
  const [showGenerateRPTModal, setShowGenerateRPTModal] = useState(false);
  const [showGenerateRPTV2Modal, setShowGenerateRPTV2Modal] = useState(false);
  const [showGenerateRPTV3Modal, setShowGenerateRPTV3Modal] = useState(false);
  const [showViewGLModal, setShowViewGLModal] = useState(false);
  const [selectedGLVersion, setSelectedGLVersion] = useState(null);

  // Load entities on mount
  useEffect(() => {
    loadEntities();
    checkApiHealth();
  }, []);

  const checkApiHealth = async () => {
    try {
      const health = await healthCheck();
      setApiStatus({ healthy: true, environment: health.environment });
    } catch (err) {
      setApiStatus({ healthy: false, environment: 'unknown' });
      console.error('API health check failed:', err);
    }
  };

  const loadEntities = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await sapApi.getEntities();
      setEntities(data.entities); // Show all entities

      // Select first enabled entity by default
      const enabledEntities = data.entities.filter(e => e.enabled);
      if (enabledEntities.length > 0 && !selectedEntity) {
        setSelectedEntity(enabledEntities[0]);
      }
    } catch (err) {
      setError('Failed to load entities: ' + err.message);
      console.error('Error loading entities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOperationComplete = () => {
    // Trigger refresh of status panel
    setRefreshTrigger(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="App">
        <DevWarningBanner />
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <DevWarningBanner />
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={loadEntities}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <DevWarningBanner />

      {/* <header className="App-header">
        <div className="header-left">
          <h1 className="app-title">Integris FinAlyzer Integration</h1>
        </div>
        <div className="header-right">
          <div className="api-status">
            <span className={`status-indicator ${apiStatus.healthy ? 'healthy' : 'unhealthy'}`}>
              {apiStatus.healthy ? '●' : '○'}
            </span>
            <span>API: {apiStatus.healthy ? 'Connected' : 'Disconnected'}</span>
            <span className="environment-badge">{apiStatus.environment.toUpperCase()}</span>
          </div>
        </div>
      </header> */}

      <div className="dashboard-container">
        {/* Sidebar Navigation */}
        <aside className="sidebar">
          <div className="sidebar-section">
            <h3 className="sidebar-title">Entity</h3>
            <EntitySelector
              entities={entities}
              selectedEntity={selectedEntity}
              onSelectEntity={setSelectedEntity}
            />
          </div>

          {/* Common Data Points (Not Entity-Specific) */}
          <div className="sidebar-section">
            <h3 className="sidebar-title">Common Data</h3>
            <nav className="sidebar-nav">
              <button
                className={`nav-item ${selectedDataPoint === 'forex' ? 'active' : ''}`}
                onClick={() => setSelectedDataPoint('forex')}
              >
                <span className="nav-icon">💱</span>
                <span className="nav-label">FX Rates (All Entities)</span>
              </button>
              <button
                className={`nav-item ${selectedDataPoint === 'intercompany' ? 'active' : ''}`}
                onClick={() => setSelectedDataPoint('intercompany')}
              >
                <span className="nav-icon">🔗</span>
                <span className="nav-label">Intercompany RPT</span>
              </button>
              <button
                className="nav-item"
                onClick={() => setShowTLRPTModal(true)}
              >
                <span className="nav-icon">📋</span>
                <span className="nav-label">TL Group RPT</span>
              </button>
            </nav>
          </div>

          {selectedEntity && (
            <>
              <div className="sidebar-section">
                <h3 className="sidebar-title">Entity Data Points</h3>
                <nav className="sidebar-nav">
                  <button
                    className={`nav-item ${selectedDataPoint === 'tb' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('tb')}
                  >
                    <span className="nav-icon">📊</span>
                    <span className="nav-label">Trial Balance</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'rpt' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('rpt')}
                  >
                    <span className="nav-icon">📈</span>
                    <span className="nav-label">RPT</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'ppe' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('ppe')}
                  >
                    <span className="nav-icon">🏗️</span>
                    <span className="nav-label">PPE</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'lease' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('lease')}
                  >
                    <span className="nav-icon">📝</span>
                    <span className="nav-label">ROU & Lease Liability</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'retained_earnings' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('retained_earnings')}
                  >
                    <span className="nav-icon">💰</span>
                    <span className="nav-label">Retained Earnings Adjustments</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'fctr' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('fctr')}
                  >
                    <span className="nav-icon">🌍</span>
                    <span className="nav-label">FCTR</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'fd_working' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('fd_working')}
                  >
                    <span className="nav-icon">📑</span>
                    <span className="nav-label">FD Working (Current/Non-current)</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'borrowings' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('borrowings')}
                  >
                    <span className="nav-icon">🏦</span>
                    <span className="nav-label">Borrowings & Interest (Current/Non-current)</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'cashflow' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('cashflow')}
                  >
                    <span className="nav-icon">💸</span>
                    <span className="nav-label">Cashflow</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'soce' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('soce')}
                  >
                    <span className="nav-icon">📋</span>
                    <span className="nav-label">SOCE</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'esop' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('esop')}
                  >
                    <span className="nav-icon">📊</span>
                    <span className="nav-label">ESOP Workings</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'ar_aging' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('ar_aging')}
                  >
                    <span className="nav-icon">📅</span>
                    <span className="nav-label">AR Ageing</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'ap_aging' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('ap_aging')}
                  >
                    <span className="nav-icon">📆</span>
                    <span className="nav-label">AP Ageing</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'ar_aging_consol' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('ar_aging_consol')}
                  >
                    <span className="nav-icon">📊</span>
                    <span className="nav-label">AR Consolidated</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'ap_aging_consol' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('ap_aging_consol')}
                  >
                    <span className="nav-icon">📊</span>
                    <span className="nav-label">AP Consolidated</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'el_ar_aging_consol' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('el_ar_aging_consol')}
                  >
                    <span className="nav-icon">🌏</span>
                    <span className="nav-label">EL AR Consolidated</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'el_ap_aging_consol' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('el_ap_aging_consol')}
                  >
                    <span className="nav-icon">🌏</span>
                    <span className="nav-label">EL AP Consolidated</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'je_adjustments' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('je_adjustments')}
                  >
                    <span className="nav-icon">📝</span>
                    <span className="nav-label">Journal Adjustments</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'consol_adjustments' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('consol_adjustments')}
                  >
                    <span className="nav-icon">📊</span>
                    <span className="nav-label">Consol Adjustments</span>
                  </button>
                </nav>
              </div>

              <div className="sidebar-section">
                <h3 className="sidebar-title">⚠️ DEV Tools</h3>
                <nav className="sidebar-nav">
                  <button
                    className={`nav-item ${selectedDataPoint === 'templates' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('templates')}
                  >
                    <span className="nav-icon">📋</span>
                    <span className="nav-label">Template Validator</span>
                  </button>
                  <button
                    className={`nav-item ${selectedDataPoint === 'query' ? 'active' : ''}`}
                    onClick={() => setSelectedDataPoint('query')}
                  >
                    <span className="nav-icon">🔍</span>
                    <span className="nav-label">SQL Query</span>
                  </button>
                </nav>
              </div>
            </>
          )}
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {/* FX Rates, Intercompany, and Template Validator are available without entity selection */}
          {selectedDataPoint === 'forex' ? (
            <div className="panels-container">
              <FXRatesPanel />
            </div>
          ) : selectedDataPoint === 'intercompany' ? (
            <div className="panels-container">
              <IntercompanyPanel />
            </div>
          ) : selectedDataPoint === 'templates' ? (
            <div className="panels-container">
              <TemplateValidationPanel />
            </div>
          ) : !selectedEntity ? (
            <div className="empty-state">
              <h2>Welcome to Integris FinAlyzer Integration</h2>
              <p>Please select an entity from the sidebar to get started, or use FX Rates / Template Validator (All Entities) for cross-entity tools.</p>
            </div>
          ) : (
            <>
              {selectedDataPoint === 'tb' && (
                <>
                  <div className="panels-container">
                    <PullTBPanel
                      entity={selectedEntity}
                      onComplete={handleOperationComplete}
                    />
                  </div>

                  <div className="status-container">
                    <StatusPanel
                      entity={selectedEntity}
                      refreshTrigger={refreshTrigger}
                      dataPoint="tb"
                    />
                  </div>
                </>
              )}

              {selectedDataPoint === 'rpt' && (
                <>
                  <div className="panels-container">
                    <FetchGLPanel
                      entity={selectedEntity}
                      onComplete={(data) => {
                        setGlInfo(data.storage);
                        handleOperationComplete();
                      }}
                    />

                    <RPTV3GLNatureMappingPanel
                      entity={selectedEntity}
                    />

                    <GLListPanel
                      entity={selectedEntity}
                      refreshTrigger={refreshTrigger}
                      onGenerateRPT={(glVersion) => {
                        setSelectedGLVersion(glVersion);
                        setShowGenerateRPTModal(true);
                      }}
                      onGenerateRPTV2={(glVersion) => {
                        setSelectedGLVersion(glVersion);
                        setShowGenerateRPTV2Modal(true);
                      }}
                      onGenerateRPTV3={(glVersion) => {
                        setSelectedGLVersion(glVersion);
                        setShowGenerateRPTV3Modal(true);
                      }}
                      onViewGL={(glVersion) => {
                        setSelectedGLVersion(glVersion);
                        setShowViewGLModal(true);
                      }}
                    />
                  </div>

                  {/* Generate RPT V1 Modal */}
                  {showGenerateRPTModal && selectedGLVersion && (
                    <GenerateRPTModal
                      entity={selectedEntity}
                      glVersion={selectedGLVersion}
                      onClose={() => {
                        setShowGenerateRPTModal(false);
                        setSelectedGLVersion(null);
                      }}
                      onComplete={(data) => {
                        setDraftInfo(data.storage);
                        setRptInfo(data.storage);
                        handleOperationComplete();
                      }}
                    />
                  )}

                  {/* Generate RPT V2 Modal (EL_SAP_B1 entities only) */}
                  {showGenerateRPTV2Modal && selectedGLVersion && (
                    <GenerateRPTV2Modal
                      entity={selectedEntity}
                      glVersion={selectedGLVersion}
                      onClose={() => {
                        setShowGenerateRPTV2Modal(false);
                        setSelectedGLVersion(null);
                      }}
                      onComplete={() => {
                        handleOperationComplete();
                      }}
                    />
                  )}

                  {/* Generate RPT V3 Modal (EL_RPT entities - rule-based) */}
                  {showGenerateRPTV3Modal && selectedGLVersion && (
                    <GenerateRPTV3Modal
                      entity={selectedEntity}
                      glVersion={selectedGLVersion}
                      onClose={() => {
                        setShowGenerateRPTV3Modal(false);
                        setSelectedGLVersion(null);
                      }}
                      onComplete={() => {
                        handleOperationComplete();
                      }}
                    />
                  )}

                  {/* View GL Modal */}
                  {showViewGLModal && selectedGLVersion && (
                    <ViewGLModal
                      entity={selectedEntity}
                      glVersion={selectedGLVersion}
                      onClose={() => {
                        setShowViewGLModal(false);
                        setSelectedGLVersion(null);
                      }}
                    />
                  )}

                </>
              )}

              {selectedDataPoint === 'ppe' && (
                <div className="panels-container">
                  <PPEManagementPanel
                    entity={selectedEntity}
                    onComplete={() => {
                      setRefreshTrigger(prev => prev + 1);
                    }}
                  />
                </div>
              )}

              {selectedDataPoint === 'query' && (
                <div className="panels-container">
                  <QueryPanel entity={selectedEntity} />
                </div>
              )}

              {selectedDataPoint === 'lease' && (
                <div className="panels-container">
                  <LeaseManagementPanel
                    entity={selectedEntity}
                    onComplete={handleOperationComplete}
                  />
                </div>
              )}

              {selectedDataPoint === 'retained_earnings' && (
                <div className="panels-container">
                  <RetainedEarningsPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'cashflow' && (
                <div className="panels-container">
                  <CashflowPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'soce' && (
                <div className="panels-container">
                  <SOCIEPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'esop' && (
                <div className="panels-container">
                  <ESOPPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'ar_aging' && (
                <div className="panels-container">
                  <ARAgingPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'ap_aging' && (
                <div className="panels-container">
                  <APAgingPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'ar_aging_consol' && (
                <div className="panels-container">
                  <ARConsolidationPanel />
                </div>
              )}

              {selectedDataPoint === 'ap_aging_consol' && (
                <div className="panels-container">
                  <APConsolidationPanel />
                </div>
              )}

              {selectedDataPoint === 'el_ar_aging_consol' && (
                <div className="panels-container">
                  <ELARConsolidationPanel />
                </div>
              )}

              {selectedDataPoint === 'el_ap_aging_consol' && (
                <div className="panels-container">
                  <ELAPConsolidationPanel />
                </div>
              )}

              {selectedDataPoint === 'je_adjustments' && (
                <div className="panels-container">
                  <JournalAdjustmentsPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'consol_adjustments' && (
                <div className="panels-container">
                  <ConsolAdjustmentsPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'fctr' && (
                <div className="panels-container">
                  <FCTRPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'fd_working' && (
                <div className="panels-container">
                  <FDManagementPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}

              {selectedDataPoint === 'borrowings' && (
                <div className="panels-container">
                  <BorrowingManagementPanel
                    entity={selectedEntity}
                    onComplete={() => setRefreshTrigger(prev => prev + 1)}
                  />
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {showTLRPTModal && (
        <TLRPTModal onClose={() => setShowTLRPTModal(false)} />
      )}

      <footer className="App-footer">
        <p>Integris FinAlyzer Integration Platform v1.0.0 | ⚠️ FOR DEVELOPMENT AND TESTING PURPOSES ONLY ⚠️</p>
      </footer>
    </div>
  );
}

export default App;
